import { NavLink, Outlet } from 'react-router-dom';

// 設定＝脇役（マスターデータ）。チャット（会話）が主役。
// 左サイドの縦メニュー。「カテゴリ（見出し）＋インデントした子項目」の2階層構造にして、
// 今後メニュー項目を増やすときは該当カテゴリの items に1行足すだけで済むようにしてある。
// to を持つ通常リンク。children を持つと「見出し＋インデントした子リンク」の入れ子グループになる。
type MenuItem = { to?: string; label: string; icon?: string; children?: MenuItem[] };
type MenuSection = { title: string; items: MenuItem[] };

const sections: MenuSection[] = [
  {
    title: 'マウント',
    items: [{ to: '/settings/mount-points', label: 'マウントポイント', icon: '📌' }],
  },
  {
    title: 'エージェント',
    items: [
      { to: '/settings/agents', label: 'エージェント', icon: '🤖' },
      { to: '/settings/teams', label: 'チーム', icon: '👥' },
      { to: '/settings/team-templates', label: 'チームテンプレート', icon: '📋' },
    ],
  },
  {
    title: 'マスタ管理',
    items: [
      { to: '/settings/system', label: 'システム設定', icon: '⚙️' },
      { to: '/settings/claude-auth', label: 'Claude認証', icon: '🔑' },
      { to: '/settings/masters', label: 'エージェント区分・責務', icon: '🤖' },
      {
        label: 'コマンド',
        icon: '⌨️',
        children: [
          { to: '/settings/commands/global', label: 'global', icon: '🌐' },
          { to: '/settings/commands/project', label: 'project', icon: '📁' },
          { to: '/settings/commands/team', label: 'team', icon: '👥' },
        ],
      },
      { to: '/settings/mount-purposes', label: '用途メモ', icon: '🏷️' },
    ],
  },
  {
    title: '実行環境',
    items: [
      { to: '/settings/devices', label: 'デバイス', icon: '🖥️' },
      { to: '/settings/paths', label: 'デバイスマウント', icon: '🧩' },
      { to: '/settings/conductors', label: 'コンダクター', icon: '🔌' },
    ],
  },
  {
    title: '案件',
    items: [
      { to: '/settings/projects', label: 'プロジェクト', icon: '📁' },
      { to: '/settings/assign', label: 'チームプロジェクトアサイン', icon: '🔗' },
      { to: '/settings/schedules', label: 'スケジュール', icon: '🗓️' },
    ],
  },
  {
    title: 'モニタ',
    items: [
      { to: '/settings/overview', label: '割当俯瞰', icon: '🗺️' },
      { to: '/settings/dashboard', label: '状態', icon: '📊' },
      { to: '/settings/request-logs', label: 'ログ', icon: '📜' },
      { to: '/settings/offline-queue', label: 'オフラインキュー', icon: '📭' },
    ],
  },
];

const item = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '7px 10px',
  borderRadius: 8,
  fontSize: 13.5,
  fontWeight: isActive ? 700 : 500,
  color: isActive ? '#1d4ed8' : '#475569',
  background: isActive ? '#eaf1ff' : 'transparent',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
});

// 入れ子グループの見出し（リンクではない）。
const groupHeader: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '7px 10px 3px',
  fontSize: 13.5,
  fontWeight: 600,
  color: '#475569',
  whiteSpace: 'nowrap',
};

// 入れ子グループの子リンク（見出しより一段インデント）。
const childItem = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
  ...item({ isActive }),
  paddingLeft: 26,
  fontSize: 13,
});

export function SettingsLayout() {
  return (
    // 全幅基調・左右余白は最小限。広いデバイス表も画面内に収める（チャットと同じく画面幅を活かす）。
    <div style={{ padding: '24px 28px 64px', maxWidth: 1680, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 16px' }}>設定</h2>
      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
        <nav
          style={{
            flexShrink: 0,
            width: 188,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            background: '#fff',
            padding: 10,
            borderRadius: 12,
            border: '1px solid #e7ebf0',
            boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            position: 'sticky',
            top: 16,
          }}
        >
          {sections.map((sec) => (
            <div key={sec.title} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  color: '#94a3b8',
                  padding: '0 10px 4px',
                  textTransform: 'uppercase',
                }}
              >
                {sec.title}
              </div>
              {sec.items.map((it) =>
                it.children ? (
                  // 入れ子グループ（例: コマンド > global/project/team）。見出し＋インデント子リンク。
                  <div key={it.label} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <div style={groupHeader}>
                      <span style={{ width: 18, textAlign: 'center', flexShrink: 0 }}>{it.icon}</span>
                      {it.label}
                    </div>
                    {it.children.map((ch) => (
                      <NavLink key={ch.to} to={ch.to!} style={childItem} end>
                        <span style={{ width: 18, textAlign: 'center', flexShrink: 0 }}>{ch.icon}</span>
                        {ch.label}
                      </NavLink>
                    ))}
                  </div>
                ) : (
                  <NavLink key={it.to} to={it.to!} style={item} end>
                    <span style={{ width: 18, textAlign: 'center', flexShrink: 0 }}>{it.icon}</span>
                    {it.label}
                  </NavLink>
                ),
              )}
            </div>
          ))}
        </nav>
        {/* minWidth:0 + overflowX で、列の多い表は内側で横スクロールさせ、画面外へはみ出させない。 */}
        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
